import { action } from 'lib/actionHelpers.js';
import { createSelector } from 'reselect';
import { formatGraphData } from 'screens/reports/lib/paidAdsHelpers';
import { sortByProps } from '../lib/sortByProp.js';

const stableArray = [];
let currentDataMap = {};
let currencyMap = ['Sales', 'Revenue', 'Spend'];

const overviewTableMap = {
  impressions: {
    friendly: 'Impressions',
  },
  conversions: { friendly: 'Conversions' },
  cpc: { friendly: 'CPC', isCurrency: true },
  roas: { friendly: 'ROAS' },
  ctr: { friendly: 'CTR' },
  clicks: { friendly: 'Clicks' },
  name: { friendly: 'Name' },
  ad_value: { friendly: 'Revenue', isCurrency: true },
  visitors: { friendly: 'Visitors' },
  sales: { friendly: 'Sales', isCurrency: true },
  spend: { friendly: 'Spend', isCurrency: true },
};

// Fields that require calculations on our end to get the correct value
const calculationsMapFacebookAds = {
  boost: ['conversions'],
  ecommerce: ['roas'],
};
function hasNumbers(t) {
  var regex = /\d/g;
  return regex.test(t);
}
const getDataValue = (campaignType, type, data) => {
  if (type === 'name' || type === 'Name') {
    return data;
  }
  // Used to calculate sales
  if (typeof data === 'string' && !hasNumbers(data)) {
    return data;
  }
  if (calculationsMapFacebookAds[campaignType] && calculationsMapFacebookAds[campaignType].includes(type)) {
    let n = Array.isArray(data) ? data.reduce((all, item) => (all += parseFloat(item.value)), 0) : 0;
    return isNaN(parseFloat(n)) ? '-' : parseFloat(n);
  }

  return isNaN(parseFloat(data)) ? '-' : parseFloat(data);
};

const totalsBarItemClickableAction = ({ name }) => {
  return () => {
    return action('setGraphTypeFacebookCampaigns', name);
  };
};

const totalsBarFormat = (item, valueMap, campaign) => {
  if (campaign) {
    return {
      name: valueMap[item[0]].friendly,
      api: valueMap[item[0]].api,
      originalName: item[0],
      value: item[1] ? getDataValue(campaign.ad_type, item[0], item[1]) : '-',
      isPlural: true,
      clickAction: valueMap[item[0]].api !== null ? totalsBarItemClickableAction({ name: item[0] }) : undefined,
      ...(currencyMap.includes(valueMap[item[0]].friendly) ? { isCurrency: true } : {}),
      order: valueMap[item[0]].order,
    };
  }
  return {};
};

const buildFacebookDataTypes = (type, includePostName = false) => {
  const baseDataTypes = {
    impressions: {
      friendly: 'Impressions',
      api: 'impressions',
      selector: 'crmFacebookSelectedCampaignImpressionssDailyData',
      order: 1,
    },
    conversions: { friendly: 'Conversions', api: null, selector: '', order: 5 },
    cpc: { friendly: 'Cost Conv', api: 'cost_per_conversion', selector: 'costPerConversionDailyStatsSelector', order: 5 },
    roas: { friendly: 'ROAS', api: null, selector: '', order: 5 },
    ctr: { friendly: 'CTR', api: 'ctr', selector: 'crmFacebookSelectedCampaignCTRDailyData', order: 5, isPercent: true },
    clicks: { friendly: 'Clicks', api: 'outbound_clicks', selector: 'crmFacebookSelectedCampaignClicksDailyData', order: 5 },
    ...(includePostName ? { name: { friendly: 'Name', api: null, selector: null, order: 5 } } : {}),
  };
  if (type === 'ecommerce') {
    delete baseDataTypes.conversions;
    delete baseDataTypes.cpc;
    delete baseDataTypes.clicks;
    baseDataTypes.spend = { friendly: 'Spend', api: 'spend', selector: 'crmFacebookSelectedCampaignSpendDailyData', order: 2 };
    baseDataTypes.ad_value = { friendly: 'Revenue', api: null, selector: '', order: 2 };
    baseDataTypes.visitors = { friendly: 'Visitors', api: null, selector: '', order: 5 };
    baseDataTypes.sales = { friendly: 'Sales', api: null, selector: '', order: 5 };
  }
  if (type === 'boost') {
    delete baseDataTypes.cpc;
    delete baseDataTypes.roas;
    delete baseDataTypes.clicks;

    baseDataTypes.spend = { friendly: 'Spend', api: 'spend', selector: 'crmFacebookSelectedCampaignSpendDailyData', order: 2 };
    baseDataTypes.visitors = { friendly: 'Visitors', api: null, selector: '', order: 5 };
    baseDataTypes.impressions.friendly = 'Brand Awareness';
    baseDataTypes.impressions.order = 1;
  }
  if (type === 'leadgen') {
    baseDataTypes.spend = { friendly: 'Spend', api: 'spend', selector: 'crmFacebookSelectedCampaignSpendDailyData', order: 2 };
    baseDataTypes.visitors = { friendly: 'Visitors', api: null, selector: '', order: 5 };
    baseDataTypes.form = { friendly: 'Forms', api: null, selector: '', order: 5 };
  }
  currentDataMap = baseDataTypes;
  return baseDataTypes;
};

const getGraphDataFormat = (dailyData, dailyDataCompare, selectedCampaignId, lineKey, secondaryLineKey, friendly) => {
  const campaign = (dailyData || []).find((item) => item.id === selectedCampaignId);
  const compareCampaign = (dailyDataCompare || []).find((item) => item.id === selectedCampaignId);
  if (campaign && (dailyDataCompare || []).length) {
    const { insights } = campaign;
    const formatted = (insights || []).map((item) => ({ ...item, Day: item.date_start, [friendly]: item[lineKey] }));
    if (compareCampaign) {
      const { insights } = compareCampaign;
      const compareFormatted =
        (insights || []).map((item) => ({
          ...item,
          Day: item.date_start,
          [friendly]: item[lineKey],
          ...(friendly === 'Impressions' ? { 'Brand Awareness': item[lineKey] } : {}),
        })) || [];
      return formatGraphData(formatted, lineKey, secondaryLineKey, compareFormatted);
    }
  }
  if (campaign && !compareCampaign) {
    const { insights } = campaign;
    const formatted = (insights || []).map((item) => {
      return {
        ...item,
        Day: item.date_start,
        [friendly]: item[lineKey],
        ...(friendly === 'Impressions' ? { 'Brand Awareness': item[lineKey] } : {}),
      };
    });
    return formatGraphData(formatted, lineKey, secondaryLineKey, []);
  }
  return [];
};

export const facebookAdCampaignsSelector = (state) => state.crmFacebookAds.facebookAdCampaigns;
export const selectedFacebookAdCampaignIdSelector = (state) => state.crmFacebookAds.selectedFacebookAdCampaign;
export const facebookCampaignsDailyDataSelector = (state) => state.crmFacebookAds.facebookCampaignsDailyData;
export const facebookCampaignsDailyDataCompareSelector = (state) => state.crmFacebookAds.facebookCampaignsDailyDataCompare;
export const facebookSelectedGraphType = (state) => state.crmFacebookAds.selectedGraphTypeFacebookCampaigns;
export const facebookCampaignAdSetSelector = (state) => state.crmFacebookAds.facebookCampaignAdSet;
export const facebookAdCampaignsCompareDataSelector = (state) => state.crmFacebookAds.facebookAdCampaignsCompareData;

const selectedGraphTypeFacebook = (state) => state.crmFacebookAds.selectedGraphTypeFacebookCampaigns;
const adwordsSelectedDataTypeForAdwordsGraph = (state) => state.crmAdwords.selectedGraphType;
const selectedAdwordsCampaignIdState = (state) => state.crmAdwords.selectedAdwordsCampaignId;

export const facebookAdCampaigns = createSelector(facebookAdCampaignsSelector, (facebookAdCampaignsSelector) => {
  return (facebookAdCampaignsSelector || stableArray).map((item) => ({
    ...item,
    label: item.name,
    value: item.id,
  }));
});

export const facebookAdCampaignsAccountOverview = createSelector(facebookAdCampaignsSelector, (facebookAdCampaignsSelector) => {
  const format = (facebookAdCampaignsSelector || stableArray).map((campaign) => {
    const valueMap = overviewTableMap;
    const salesValue = (campaign.conversions || []).reduce((all, item) => {
      if (item.action_type.includes('omni_purchase')) {
        all += parseInt(item.value);
      }
      return all;
    }, 0);
    campaign.sales = (salesValue || 0).toFixed(0);
    const entries = Object.entries(campaign)
      .filter((item) => valueMap[item[0]])
      .map((item) => totalsBarFormat(item, valueMap, campaign));

    const formatted = entries.reduce((all, item) => {
      if (item.name === 'Name') {
        all[item.name] = {};
      }
      all.Name = { ...all.Name, [item.name]: item.value };
      return all;
    }, {});
    return { ...formatted.Name };
  });

  return format;
});

export const selectedFacebookAdCampaignTotalsBarData = createSelector(selectedFacebookAdCampaignIdSelector, facebookAdCampaigns, facebookAdCampaignsCompareDataSelector, facebookAdCampaignsAccountOverview, (selectedFacebookAdCampaignIdSelector, facebookAdCampaigns, facebookAdCampaignsCompareData, facebookAdCampaignsAccountOverview) => {
  let final = [];
  let compareFinal = [];
  const accountTotals = (facebookAdCampaignsAccountOverview || []).reduce((all, item, i) => {
    if (i === 0) {
      all.Spend = 0;
      all.Revenue = 0;
      all['Brand Awareness'] = 0;
    }
    all['Brand Awareness'] = all['Brand Awareness'] += parseInt(item.Impressions);
    all.Revenue = all.Revenue += parseInt(item.Revenue);
    all.Spend = all.Spend += parseInt(item.Spend);
    return all;
  }, {});
  const campaign = (facebookAdCampaigns || []).find((item) => item.id === selectedFacebookAdCampaignIdSelector);
  if (campaign) {
    const valueMap = buildFacebookDataTypes(campaign.ad_type);
    final = Object.entries(campaign)
      .filter((item) => valueMap[item[0]])
      .map((item) => totalsBarFormat(item, valueMap, campaign))
      .map((item) => {
        const accountTotal = accountTotals[item.name];
        if (accountTotal) {
          const valueFormatted = typeof item.value === 'string' ? parseInt((item.value || '').replace('$', '').replace(',', '').trim()) : item.value;
          item.accountTotalPercentage = ((valueFormatted / accountTotal) * 100).toFixed(1);
        }
        return item;
      });
  }

  const compareCampaign = (facebookAdCampaignsCompareData || []).find((item) => item.id === selectedFacebookAdCampaignIdSelector);
  if ((facebookAdCampaignsCompareData || []).length && compareCampaign) {
    const valueMap = buildFacebookDataTypes(compareCampaign.ad_type);
    compareFinal = Object.entries(compareCampaign)
      .filter((item) => valueMap[item[0]])
      .map((item) => totalsBarFormat(item, valueMap, campaign));
  }
  if ((final || []).length && (compareFinal || []).length) {
    const compareDateValues = compareFinal.reduce((all, item) => {
      all[item.name] = parseInt(item.value);
      return all;
    }, {});
    const calculated = final.map((item) => {
      const compareDateValue = compareDateValues[item.name];
      if (compareDateValues[item.name]) {
        item.change = item.value - compareDateValue;
      }
      return item;
    }, {});
    const set = calculated.map((n) => ({ ...n, value: isNaN(parseFloat(n.value)) ? '-' : parseFloat(n.value).toLocaleString() }));
    return set.sort(sortByProps(['order'], ['asc']));
  }
  const set = final.map((item) => ({ ...item, value: isNaN(parseFloat(item.value)) ? '-' : parseFloat(item.value).toLocaleString() }));
  return set.sort(sortByProps(['order'], ['asc']));
});

export const facebookAdsAccountOverviewTotalsBar = createSelector(facebookAdCampaigns, (facebookAdCampaigns) => {
  const formatted = (facebookAdCampaigns || []).map((campaign) => {
    const valueMap = buildFacebookDataTypes(campaign.ad_type);
    return Object.entries(campaign)
      .filter((item) => valueMap[item[0]])
      .map((item) => totalsBarFormat(item, valueMap, campaign));
  });
  const flattened = (formatted || []).flat();
  const valueMap = flattened.reduce((all, item) => {
    const { value } = item;
    if (!all[item.originalName]) {
      all[item.originalName] = { ...item, value: 0 };
    }
    if (item.value !== '-') {
      let currentValue = all[item.originalName].value;
      let calculated = (currentValue += parseFloat(value));
      all[item.originalName].value = calculated;
    }
    return all;
  }, {});

  return Object.entries(valueMap).map((item) => ({ ...item[1], value: item[1].value.toLocaleString(), clickAction: undefined }));
});

export const crmFacebookSelectedCampaignClicksDailyData = createSelector(selectedFacebookAdCampaignIdSelector, facebookCampaignsDailyDataSelector, facebookCampaignsDailyDataCompareSelector, (selectedFacebookAdCampaignIdSelector, facebookCampaignsDailyDataSelector, compareData) => {
  return getGraphDataFormat(facebookCampaignsDailyDataSelector, compareData, selectedFacebookAdCampaignIdSelector, 'clicks', 'clicks', 'Clicks');
});

export const crmFacebookSelectedCampaignImpressionssDailyData = createSelector(selectedFacebookAdCampaignIdSelector, facebookCampaignsDailyDataSelector, facebookCampaignsDailyDataCompareSelector, (selectedFacebookAdCampaignIdSelector, facebookCampaignsDailyDataSelector, compareData) => {
  return getGraphDataFormat(facebookCampaignsDailyDataSelector, compareData, selectedFacebookAdCampaignIdSelector, 'impressions', 'impressions', 'Impressions');
});
export const crmFacebookSelectedCampaignCTRDailyData = createSelector(selectedFacebookAdCampaignIdSelector, facebookCampaignsDailyDataSelector, facebookCampaignsDailyDataCompareSelector, (selectedFacebookAdCampaignIdSelector, facebookCampaignsDailyDataSelector, compareData) => {
  return getGraphDataFormat(facebookCampaignsDailyDataSelector, compareData, selectedFacebookAdCampaignIdSelector, 'ctr', 'ctr', 'CTR');
});

export const crmFacebookSelectedCampaignSpendDailyData = createSelector(selectedFacebookAdCampaignIdSelector, facebookCampaignsDailyDataSelector, facebookCampaignsDailyDataCompareSelector, (selectedFacebookAdCampaignIdSelector, facebookCampaignsDailyDataSelector, compareData) => {
  return getGraphDataFormat(facebookCampaignsDailyDataSelector, compareData, selectedFacebookAdCampaignIdSelector, 'spend', 'spend', 'Spend');
});

export const facebookDataMapSelector = createSelector(facebookSelectedGraphType, selectedFacebookAdCampaignTotalsBarData, adwordsSelectedDataTypeForAdwordsGraph, selectedFacebookAdCampaignIdSelector, selectedAdwordsCampaignIdState, (facebookSelectedGraphType, selectedFacebookAdCampaignTotalsBarData, adwordsSelectedDataTypeForAdwordsGraph, selectedFacebookAdCampaignIdSelector, selectedAdwordsCampaignIdState) => {
  return currentDataMap[facebookSelectedGraphType] || {};
});

export const facebookCampaignAdSet = createSelector(facebookCampaignAdSetSelector, (facebookCampaignAdSetSelector) => {
  const removeNullAndZeroValues = (facebookCampaignAdSetSelector || []).map((item) => {
    return Object.fromEntries(
      Object.entries(item).map((item) => {
        if (item[1] === null || item[1] === '0' || item[1] === 0) {
          item[1] = '-';
        }
        if (item[0] === 'conversions' && item[1] === '-') {
          item[1] = [];
        }
        if (item[0] === 'conversion_values' && item[1] === '-') {
          item[1] = [];
        }
        return item;
      })
    );
  });

  const includingCalculatedConversions = (removeNullAndZeroValues || []).reduce((all, item) => {
    let test = { ...item };
    test.conversions = (item.conversions || []).reduce((formattedAdSetData, adSetData) => {
      formattedAdSetData += parseFloat(adSetData.value);
      return formattedAdSetData;
    }, 0);
    all.push(test);
    return all;
  }, []);

  return includingCalculatedConversions;
});

export const crmTotalsBarSelection = createSelector(selectedGraphTypeFacebook, adwordsSelectedDataTypeForAdwordsGraph, selectedFacebookAdCampaignIdSelector, selectedAdwordsCampaignIdState, (selectedGraphTypeFacebook, adwordsSelectedDataTypeForAdwordsGraph, selectedFacebookAdCampaignIdSelector, selectedAdwordsCampaignIdState) => {
  if (selectedAdwordsCampaignIdState || selectedFacebookAdCampaignIdSelector) {
    return {
      selectedGraphTypeFacebook: selectedGraphTypeFacebook,
      selectedGraphTypeAdwords: adwordsSelectedDataTypeForAdwordsGraph,
    };
  }
  return {};
});
